/**
 * @fileoverview Hook Data Layer para carga y paginación de órdenes de cliente en Inbox.
 * @module useCustomerOrdersData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import orderShopModelMC from '../../../models/OrderShopModelMC';
import { LOAD_DATA_FAILURE } from 'actions/inbox/conversationActions';

/**
 * @typedef {Object} UseCustomerOrdersDataResult
 * @property {Array} orders - Lista de órdenes del cliente
 * @property {boolean} loading - Estado de carga
 * @property {boolean} viewMore - Si hay más páginas para cargar
 * @property {function} handleViewMore - Handler para paginación
 * @property {function} reloadOrders - Recarga la lista de órdenes (reset)
 * @property {function} updateOrderInList - Actualiza la información de una orden en el estado local
 */

/**
 * Hook Data Layer para carga y paginación de órdenes de cliente en Inbox.
 *
 * - Solo gestiona carga de datos, paginación y estados de loading/error.
 * - NO maneja UI ni lógica de presentación.
 *
 * @param {Object} customerData - Datos del customer y conversación serializada
 * @returns {UseCustomerOrdersDataResult} Objeto con datos, estados y handlers para gestión de órdenes
 *
 * @see useCustomerOrdersUI
 */
export default function useCustomerOrdersData(customerData) {
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const viewMore = useMemo(() => page < totalPages, [page, totalPages]);

  /**
   * Carga las órdenes del cliente. Si reset=true, reemplaza la lista; si no, concatena (paginación).
   * @function loadOrdersShop
   * @memberof useCustomerOrdersData
   * @param {boolean} reset - Si true, reemplaza la lista; si false, concatena
   */
  const loadOrdersShop = useCallback(
    async (reset = false) => {
      if (!customerData?.web_id) return;
      setLoading(true);
      try {
        const response = await orderShopModelMC.get({
          'q[customer_web_id_in_mercately_eq]': customerData.web_id,
          page,
        });
        setTotalPages(response.total_pages);
        if (reset) {
          setOrders(response.orders);
        } else {
          setOrders((prevOrders) => [...prevOrders, ...response.orders]);
        }
      } catch (error) {
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: error.message || 'Error loading customer orders.',
        });
      } finally {
        setLoading(false);
      }
    },
    [customerData?.web_id, page, dispatch, setLoading, setOrders]
  );

  // Carga inicial y cuando cambia el customer
  useEffect(() => {
    if (customerData?.web_id) {
      setPage(1);
      loadOrdersShop(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerData?.web_id]);

  // Paginación: cuando page > 1
  useEffect(() => {
    if (page > 1) {
      loadOrdersShop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  /**
   * Handler para cargar más páginas
   * @function handleViewMore
   * @memberof useCustomerOrdersData
   */
  const handleViewMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  /**
   * Recarga la lista de órdenes (reset)
   * @function reloadOrders
   * @memberof useCustomerOrdersData
   */
  const reloadOrders = useCallback(() => {
    setPage(1);
    loadOrdersShop(true);
  }, [loadOrdersShop]);

  /**
   * Actualiza la información de una orden en el estado local por id.
   *
   * @function updateOrderInList
   * @memberof useCustomerOrdersData
   * @description
   * Busca la orden por id y reemplaza sus datos en el array local de órdenes.
   * Permite reflejar cambios de estado, pago u otros campos tras una actualización remota.
   *
   * @param {Object} updatedOrder - Objeto de orden actualizado (debe incluir id)
   *
   * @example
   * updateOrderInList({ id: 123, status: 'success' });
   */
  const updateOrderInList = useCallback((updatedOrder) => {
    setOrders((prevOrders) => prevOrders.map(order =>
      order.id === updatedOrder.id ? updatedOrder : order
    ));
  }, []);

  return useMemo(
    () => ({
      orders,
      loading,
      viewMore,
      handleViewMore,
      reloadOrders,
      updateOrderInList,
    }),
    [orders, loading, viewMore, handleViewMore, reloadOrders, updateOrderInList]
  );
}
